import { Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { CheckCircle2, RefreshCw, Trash2, XCircle } from 'lucide-react';
import React from 'react';
import type { ApiKey } from '../../types';
import { useTranslation } from 'react-i18next';
import { ApiScopeBadge, CapabilityBadge } from './ScopeBadges';
import {
  apiKeyEnvironment,
  apiKeyScopeCount,
  displayName,
  formatDate,
  formatOptionalDate,
  formatRateLimit,
} from './utils';

export interface ApiKeyDetailDialogProps {
  apiKey: ApiKey | null;
  open: boolean;
  onClose: () => void;
  onRotate: (apiKey: ApiKey) => void;
  onRevoke: (apiKey: ApiKey) => void;
}

export const ApiKeyDetailDialog: React.FC<ApiKeyDetailDialogProps> = ({
  apiKey,
  open,
  onClose,
  onRotate,
  onRevoke,
}) => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'vi' ? 'vi-VN' : 'en-US';

  if (!apiKey) return null;

  const isActive = apiKey.status === 'ACTIVE';
  const details = [
    { label: t('apiKeys.detail.publicId'), value: apiKey.publicId, mono: true },
    { label: t('apiKeys.card.prefix'), value: apiKey.prefix || '—', mono: true },
    { label: t('apiKeys.card.environment'), value: apiKeyEnvironment(apiKey) },
    { label: t('apiKeys.table.columns.created'), value: formatDate(apiKey.createdAt, dateLocale) },
    { label: t('apiKeys.card.lastUsed'), value: formatOptionalDate(apiKey.lastUsed, dateLocale) },
    { label: t('apiKeys.card.expires'), value: formatOptionalDate(apiKey.expiresAt, dateLocale) },
    {
      label: t('apiKeys.detail.revokedAt'),
      value: formatOptionalDate(apiKey.revokedAt, dateLocale),
    },
    { label: t('apiKeys.card.rateLimit'), value: formatRateLimit(apiKey) },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle className="flex flex-col gap-2 border-b border-gray-200 dark:border-slate-700">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="truncate text-lg font-semibold text-gray-900 dark:text-slate-100">
              {displayName(apiKey)}
            </div>
            <div className="truncate font-mono text-xs font-normal text-gray-400 dark:text-slate-500">
              {apiKey.publicId}
            </div>
          </div>
          <Chip
            label={t(`apiKeys.status.${apiKey.status}`, { defaultValue: apiKey.status })}
            size="small"
            color={isActive ? 'success' : 'default'}
            icon={isActive ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
          />
        </div>
      </DialogTitle>
      <DialogContent className="!pt-4">
        <div className="grid gap-3 sm:grid-cols-2">
          {details.map((item) => (
            <div
              key={item.label}
              className="min-w-0 rounded-lg border border-gray-200 p-3 dark:border-slate-700"
            >
              <div className="text-xs text-gray-400 dark:text-slate-500">{item.label}</div>
              <div
                className={`mt-1 truncate text-sm text-gray-800 dark:text-slate-200 ${
                  item.mono ? 'font-mono' : ''
                }`}
              >
                {item.value}
              </div>
            </div>
          ))}
        </div>

        {apiKey.metadata?.description && (
          <div className="mt-4 rounded-lg border border-gray-200 p-3 dark:border-slate-700">
            <div className="text-xs text-gray-400 dark:text-slate-500">
              {t('apiKeys.detail.description')}
            </div>
            <p className="mt-1 text-sm text-gray-700 dark:text-slate-300">
              {apiKey.metadata.description}
            </p>
          </div>
        )}

        <div className="mt-4">
          <div className="mb-2 text-sm font-semibold text-gray-800 dark:text-slate-200">
            {apiKey.scopes?.length ? t('apiKeys.detail.scopes') : t('apiKeys.detail.capabilities')}
          </div>
          {apiKeyScopeCount(apiKey) === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-500 dark:border-slate-600 dark:text-slate-400">
              {t('apiKeys.card.noCapabilities')}
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {apiKey.scopes?.length
                ? apiKey.scopes.map((scope) => (
                    <ApiScopeBadge
                      key={`${scope.scope}:${scope.resourceType ?? ''}:${scope.resourceId ?? ''}`}
                      scope={scope}
                    />
                  ))
                : apiKey.capabilities.map((capability) => (
                    <CapabilityBadge key={capability.capabilityCode} scope={capability} />
                  ))}
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions className="border-t border-gray-200 dark:border-slate-700">
        <Button onClick={onClose}>{t('apiKeys.detail.close')}</Button>
        {isActive && (
          <>
            <Button
              type="button"
              variant="outlined"
              startIcon={<RefreshCw size={16} />}
              onClick={() => onRotate(apiKey)}
            >
              {t('apiKeys.card.rotate')}
            </Button>
            <Button
              type="button"
              color="error"
              variant="contained"
              startIcon={<Trash2 size={16} />}
              onClick={() => onRevoke(apiKey)}
            >
              {t('apiKeys.revoke.revokeKey')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
